import { CreateApolloClient } from '@/api/client/apollo';
import { useSession } from '@/providers/session/SessionProvider';
import React from 'react';
import { ARCHIVE_HOMEWORK, ArchiveHomeworkResponseType, CREATE_HOMEWORK, CreateHomeworkResponse, LESSON_PUBLISH, LessonPublishResponse } from '../gql';
import { ClassModel } from '../types/homework';
export default function useHomework() {
    const { session } = useSession();
    const client = React.useMemo(() => CreateApolloClient(session?.token), [session?.token]);

    const publish = async (values: {
        name: string;
        lessonId: string;
        lessonName: string;
        class: ClassModel[];
        writingHints: boolean;
        freeStyleMode: boolean;
        startTime: Date | null;
        endTime: Date | null;
    }) => {
        try {
            const result = await client.mutate<LessonPublishResponse>({
                mutation: LESSON_PUBLISH,
                variables: { lessonID: values.lessonId },
            });
            if (!result || !result?.data?.publishLesson) {
                throw new Error('UPDATE_FAILED');
            }
            // This can set the time to 00:00:00
            const startTime = new Date(values.startTime || 0);
            startTime.setHours(0, 0, 0, 0);

            // This can set the time to 00:00:00
            const endTime = new Date(values.endTime || 0);
            endTime.setHours(23, 59, 59, 999);

            const res = await client.mutate<CreateHomeworkResponse>({
                mutation: CREATE_HOMEWORK,
                variables: {
                    name: values.name,
                    lessonVersion: result?.data?.publishLesson.id,
                    class: { connect: values.class.map((c) => ({ id: c.id })) },
                    writingHints: values.writingHints,
                    freeStyleMode: values.freeStyleMode,
                    startTime: startTime,
                    endTime: endTime,
                },
            });
            if (!res || !res?.data?.createHomework) {
                throw new Error(res?.errors?.[0]?.message || 'Failed to create homework. Please try again.');
            }
            return res?.data?.createHomework;
        } catch (error) {
            console.error('Error publishing homework:', error);
            throw error;
        }
    };

    const remove = async (id: string[]) => {
        const data = id.map((id) => ({ where: { id }, data: { isArchived: true } }));
        await client.mutate<ArchiveHomeworkResponseType>({
            mutation: ARCHIVE_HOMEWORK,
            variables: {
                data,
            },
        });
    };

    return { publish, remove };
}
